import { useEffect, useRef, useId, useState } from "react";
import { getCookie } from "cookies-next";
import { useQueryClient } from "@tanstack/react-query";
import { Env } from "@/Env";
import { capitalize } from "@/utils/CommonUtils";
import { resolveCheckoutToken } from "@/api/apis";
import { Button } from "@/v2-components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/v2-components/ui/dialog";

declare global {
  interface Window {
    Npay?: {
      order: {
        create: (config: NpayOrderConfig) => void;
      };
    };
  }
}

interface NpayOrderConfig {
  buttonKey: string;
  containerId: string;
  orderRegistrationVersion: string;
  type: "template" | "custom";
  colorTheme?: "green" | "white";
  enable?: boolean;
  components?: {
    talkTalk?: boolean;
    wishlist?: boolean;
    benefitMessage?: boolean;
    benefitCoachMark?: boolean;
  };
  onBuyClick: () => Promise<{ key: string; merchantNo: string } | null>;
  onWishlistClick?: () => Promise<{
    merchantId: string;
    payProductId: string;
  }>;
}

export interface NaverPaySelectedOption {
  name: string; // "색상" | "사이즈"
  id: string; // "onyx" | "small"
  text: string; // "Onyx" | "Small"
}

export interface NaverPayItem {
  productId: string;
  slug: string;
  name: string;
  basePrice: number;
  quantity: number;
  imageUrl: string;
  infoUrl: string;
  sku?: string;
  selectedOptions?: NaverPaySelectedOption[];
}

interface NaverPayButtonProps {
  items: NaverPayItem[];
  /** 옵션 미선택 등 유효성 검증. false 반환 시 주문 진행 안 함 */
  onValidate?: () => boolean;
  /**
   * SDK 가 **실제로 버튼을 그린 뒤** 1회 호출. 호출처가 "결제 수단이 존재한다"를 알기 위한
   * 유일한 신호다.
   *
   * ⚠️ 게이트(`useNaverPayEnabled`)는 **설정만** 본다(국가·버튼키·허용목록). SDK 로드는
   * 아래 `catch` 가 삼키고 컨테이너는 무조건 렌더되므로, 로드가 실패해도 호출처는 그 사실을
   * 알 수 없다. 애드블로커/프라이버시 확장이 결제 SDK 도메인을 막는 건 드물지 않고, 그
   * 사용자에게는 **영구**다(같은 클래스의 기록: `pdp-checkout-buttons.tsx` 의 auth 차단 주석).
   * 부수 UI(예: `Express checkout` 구분선)를 이 신호에 걸면 "수단 없는 라벨"이 안 남는다.
   */
  onReady?: () => void;
  /**
   * 카트가 적용 중인 발급 쿠폰 id(#5170). 서버(`register.ts`)가 토큰으로 다시 조회해 계산한다 — 금액은 보내지 않는다.
   * 쿠폰이 빠진 `items[].basePrice` 가 아니라 **쿠폰 반영 단가**를 함께 보내야 서버의 화면가 대조가 맞는다.
   */
  couponIssueIds?: string[];
}

export const NAVERPAY_OPTION_NAME_MAP: Record<string, string> = {
  color: "색상",
  size: "사이즈",
};

/**
 * CartItemOption[] → NaverPaySelectedOption[] 변환
 * options[0] = color, options[1] = size (bhaptics 규칙)
 */
export function toNaverPayOptions(
  options: Array<{ type: string; value: string }>,
): NaverPaySelectedOption[] {
  return options.map((opt) => ({
    name: NAVERPAY_OPTION_NAME_MAP[opt.type] ?? opt.type,
    id: opt.value,
    text: capitalize(opt.value),
  }));
}

let sdkPromise: Promise<void> | null = null;

function loadNpaySDK(): Promise<void> {
  if (window.Npay) return Promise.resolve();
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = Env.NaverPay.ButtonSdkUrl;
    script.onload = () => resolve();
    script.onerror = () => {
      sdkPromise = null;
      reject(new Error("Failed to load Npay SDK"));
    };
    document.head.appendChild(script);
  });
  return sdkPromise;
}

type NaverPayErrorKind =
  "generic" | "outOfStock" | "priceChanged" | "couponReserved";

const ERROR_COPY: Record<NaverPayErrorKind, { title: string; body: string }> = {
  generic: {
    title: "주문 오류",
    body: "주문 처리 중 오류가 발생했습니다. 다시 시도해 주세요.",
  },
  outOfStock: {
    title: "재고 부족",
    body: "선택하신 상품의 재고가 부족하여 주문할 수 없습니다.",
  },
  // 서버가 화면에 보인 값보다 비싸게 청구하게 될 때(할인 종료 · 가격 변경) — `register.ts` 화면가 대조.
  priceChanged: {
    title: "가격 변경",
    body: "할인 종료 등으로 가격이 바뀌었습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.",
  },
  // 같은 쿠폰으로 진행 중인 다른 결제가 있다 — mgmt 결제 예약(보상판매 쿠폰, #5170 결정 2026-11-03). 예약 시간은 mgmt 가
  // 정하고 레일마다 다르므로(자체 결제 15분 · 네이버페이는 더 길게) 분 단위를 적지 않는다.
  // 「다른 결제」라고 하지 않는다 — mgmt 가 같은 사용자의 재시도를 직전 예약에 묶어 막는 동안은 방금 닫은 자기 결제창이
  // 원인이다(#6115 R2 — docs/checkout-coupon-policy.md §3-6).
  couponReserved: {
    title: "쿠폰 사용 중",
    body: "이 쿠폰으로 진행 중인 결제가 있습니다(방금 열었다 닫은 결제창 포함). 잠시 기다린 뒤 다시 시도해 주세요.",
  },
};

class OutOfStockError extends Error {
  constructor() {
    super("Out of stock");
    this.name = "OutOfStockError";
  }
}

class PriceChangedError extends Error {
  constructor() {
    super("Price changed");
    this.name = "PriceChangedError";
  }
}

class CouponReservedError extends Error {
  constructor() {
    super("Coupon reserved");
    this.name = "CouponReservedError";
  }
}

async function registerNaverPayOrder(
  items: NaverPayItem[],
  token: string | undefined,
  couponIssueIds: string[] | undefined,
): Promise<{ buyKey: string; merchantNo: string }> {
  const returnUrl = window.location.origin + window.location.pathname;
  const response = await fetch("/api/naverpay/register/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      items,
      returnUrl,
      cpaInflowCode: getCookie("CPAValidator"),
      naverInflowCode: getCookie("NA_CO"),
      saClickId: getCookie("NVADID"),
      // 비어 있으면 `register.ts` 가 빈 authorization 으로 mgmt 에 넘겨 **게스트** draft 가 된다.
      token: token ?? "",
      ...(couponIssueIds?.length && { couponIssueIds }),
    }),
  });

  if (response.status === 409) {
    // 409 는 셋이다 — 재고 부족(`Out of stock`) · 화면가보다 비싼 청구(`PriceChanged`) · 쿠폰 결제 예약(`CouponReserved`).
    // 본문으로 가른다.
    const body = await response.json().catch(() => null);
    if (body?.error === "PriceChanged") throw new PriceChangedError();
    if (body?.error === "CouponReserved") throw new CouponReservedError();
    throw new OutOfStockError();
  }
  if (!response.ok) {
    throw new Error("NaverPay order registration failed");
  }

  return response.json();
}

export default function NaverPayButton({
  items,
  onValidate,
  onReady,
  couponIssueIds,
}: NaverPayButtonProps) {
  const containerId = useId().replace(/:/g, "-") + "-npay-btn";
  const initializedRef = useRef(false);
  const itemsRef = useRef(items);
  itemsRef.current = items;
  // 버튼 핸들러는 마운트 때 한 번만 만든다 — 최신 쿠폰은 ref 로 읽는다. 갱신은 **effect 에서** 한다: 렌더 중 ref 쓰기는
  // `react-hooks/refs` 가 잡고(위 두 줄은 기준선 `eslint-suppressions.json` 에 있다), 클릭은 커밋 뒤라 effect 값이 최신이다.
  const couponIssueIdsRef = useRef(couponIssueIds);
  useEffect(() => {
    couponIssueIdsRef.current = couponIssueIds;
  }, [couponIssueIds]);
  const onValidateRef = useRef(onValidate);
  onValidateRef.current = onValidate;
  // ⚠️ **최초 값만 잡는다**(위 두 ref 와 달리 매 렌더 갱신하지 않는다). 이 신호는 마운트
  // 1회짜리라 갱신할 이유가 없고, 렌더 중 ref 쓰기는 `react-hooks/refs` 가 잡는다
  // (위 두 줄은 그 규칙의 기존 위반이다 — 여기서 하나 더 늘리지 않는다).
  // effect deps 에도 넣지 않는다 — 부모의 인라인 콜백 때문에 SDK 를 재생성하면 안 된다
  // (아래 주석: dispose 가 비공개라 타이머·리스너가 누적된다).
  const onReadyRef = useRef(onReady);
  // 토큰은 훅(`useUser().token`)에서 읽지 않는다 — `onBuyClick` 이 누른 시점에 다시 읽는다(아래).
  const queryClient = useQueryClient();
  const [showError, setShowError] = useState<NaverPayErrorKind | null>(null);

  // ⚠️ 이 버튼은 **마운트 시 1회만** 그려진다. SDK 가 `window.Npay.order.create` 하나만
  // 노출하고 update/destroy/setEnable 이 없어서(npay.button.js 실측), 생성 후에 활성 상태를
  // 바꿀 방법이 없다. 그래서 `disabled` prop 을 두지 않는다 —
  // **결제 가능 여부는 호출처가 조건부 마운트로 표현할 것**(cart-page, ProductNaverPayButton).
  //
  // 컨테이너를 innerHTML="" 로 비우고 재생성하는 우회는 쓰지 말 것. SDK 내부 dispose() 가
  // 비공개라 3초 setInterval(혜택 문구 회전)과 window resize 리스너가 안 죽고, 옛 렌더러가
  // **같은 DOM 요소를 계속 붙잡아** 새 버튼을 조작한다(토글할 때마다 하나씩 누적).
  useEffect(() => {
    if (initializedRef.current) return;
    if (!Env.NaverPay.ButtonKey) return;

    let mounted = true;

    loadNpaySDK()
      .then(() => {
        if (!mounted || !window.Npay) return;
        initializedRef.current = true;

        window.Npay.order.create({
          buttonKey: Env.NaverPay.ButtonKey,
          containerId,
          orderRegistrationVersion: "2.1",
          type: "template",
          colorTheme: "green",
          enable: true,
          components: {
            talkTalk: false,
            wishlist: false,
            benefitMessage: true,
            benefitCoachMark: true,
          },
          onBuyClick: async () => {
            if (onValidateRef.current && !onValidateRef.current()) {
              return null;
            }
            try {
              // ★회원 여부는 **누른 이 시점에 다시 읽는다** — 카트 결제(`cart-checkout-button.tsx` 의 같은 자리 주석)와
              // 같은 해석·같은 reject 처리다. 종전엔 훅의 `useUser().token` 을 매 렌더 ref 에 담아 실어서, 토큰 조회가
              // 일시 장애로 끝난 회원(모름 — docs/pitfalls.md #70 `useUser().token` 은 셋이다)과 첫 조회 중에 누른 회원이
              // 빈 토큰으로 **게스트 draft** 를 만들었다(`register.ts` 는 받은 토큰을 그대로 mgmt 에 넘긴다). reject 를 안
              // 받으면 아래 catch 가 「주문 오류」를 띄워 결제가 막힌다.
              //
              // ⚠️ 첫 조회(`isTokenLoading`)를 기다리지 않는다 — 이 버튼은 잠글 수 없고(위 「마운트 시 1회만」) 기다리면
              // 누른 뒤 최대 첫 조회 상한만큼 멈춘다. 근거: docs/checkout-auth-embed-policy.md 「`fetchToken` 은 일시
              // 실패를 반드시 throw 한다」.
              const token = await resolveCheckoutToken(queryClient).catch(
                () => undefined,
              );
              const result = await registerNaverPayOrder(
                itemsRef.current,
                token,
                couponIssueIdsRef.current,
              );
              return {
                key: result.buyKey,
                merchantNo: result.merchantNo,
              };
            } catch (error) {
              console.error("NaverPay order registration error:", error);
              setShowError(
                error instanceof OutOfStockError
                  ? "outOfStock"
                  : error instanceof PriceChangedError
                    ? "priceChanged"
                    : error instanceof CouponReservedError
                      ? "couponReserved"
                      : "generic",
              );
              return null;
            }
          },
        });

        // ★버튼이 실제로 그려진 뒤에만 알린다. `create` 가 던지면 아래 `catch` 로 가므로
        // 이 줄은 실행되지 않는다 = 실패는 신호가 없다(호출처가 부수 UI 를 안 그린다).
        onReadyRef.current?.();
      })
      .catch((error) => {
        console.error("NaverPay SDK load error:", error);
      });

    return () => {
      mounted = false;
    };
    // `queryClient` 는 앱 수명 동안 같은 인스턴스다(`_app.tsx` 의 `useState`) — deps 에 둬도 SDK 를 다시 만들지
    // 않는다(`initializedRef`).
  }, [containerId, queryClient]);

  return (
    <>
      <div id={containerId} />
      <Dialog open={showError !== null} onOpenChange={() => setShowError(null)}>
        <DialogContent className="flex min-h-[200px] max-w-[400px] flex-col p-8">
          <DialogHeader>
            <DialogTitle>
              {showError ? ERROR_COPY[showError].title : ""}
            </DialogTitle>
            <DialogClose />
          </DialogHeader>
          <DialogDescription className="flex-1">
            {showError ? ERROR_COPY[showError].body : ""}
          </DialogDescription>
          <DialogFooter className="w-full items-center justify-end gap-2">
            <Button
              size="sm"
              variant="blue-fill"
              shape="pill"
              className="w-[100px]"
              onClick={() => setShowError(null)}
            >
              확인
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
